<template>
  <div v-if="shouldShowRecentlyViewed" class="main-space">
    <LegacyWrap>
      <div class="title">{{ t('title') }}</div>
    </LegacyWrap>
    <div class="swiper-main-container">
      <UiSlider v-bind="cardProps" :key="cardExperiences.length" arrows-always-visible>
        <template #skeleton><ExpCardRedesignSkeleton /></template>
        <template #slide="{ slide, index }">
          <ExpCardRedesign
            :exp="slide"
            :tooltip-teleport-target="teleportTarget"
            size="s"
            :exp-snippet-index="index"
            sogu-page="main"
            sogu-block="recently-watched"
            :is-in-favorites="isExperienceFavorite(slide.id)"
            is-show-favorite-btn
            data-test-prefix="viewed-item"
            @toggle-favorite="handleToggleFavorite"
          />
        </template>
        <template #rightArrowIcon>
          <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
        <template #leftArrowIcon>
          <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
      </UiSlider>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getDisplayCurrencyQuery } from '@experience/core-display-currency-lib';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { UiSlider } from '@experience/core-slider-ui';
import { ExpCardRedesign, ExpCardRedesignSkeleton } from '@experience/exp-card';
import { useI18n } from '@experience/i18n';
import { sendFavoriteMindboxEvent } from '@experience/mindbox-events';
import { computed, onMounted, ref } from 'vue';

import { useViewHistory } from '@/features/recentlyViewed/composables/useViewHistory';
import ru from '@/features/recentlyViewed/i18n/ru';
import type { Experience, ExperienceFavoriteItem } from '@/shared/api/generated/api';
import {
  addExperienceToFavorite,
  getExperiencesByIds,
  getExperiencesFavoritesList,
  removeExperienceFromFavorite,
} from '@/shared/api/generated/api';
import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useCarouselConfig } from '@/shared/lib/carousel/useCarouselConfig';
import { mapExperienceToExpCard } from '@/shared/lib/experience/mapExperienceToExpCard';
import { useMovementTypeLabels } from '@/shared/lib/experience/useMovementTypeLabels';
import { initModalTeleportTarget } from '@/shared/lib/initModalTeleportTarget';

import type { Experience as ExpCardExperience } from '@experience/exp-card/dist/types/experience';

// Контейнер нужен до монтирования карточек: тултип переносит DOM при инициализации.
const tooltipTeleportTarget = ref<HTMLElement | null>(null);
initModalTeleportTarget(tooltipTeleportTarget);

const teleportTarget = computed(() => tooltipTeleportTarget.value ?? undefined);

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const rawExperiences = ref<Experience[]>([]);
const favorites = ref<ExperienceFavoriteItem[]>([]);
const loading = ref(true);
const isViewed = ref(true);
const { movementTypeLabels } = useMovementTypeLabels();

// Блокирует повторное переключение избранного до завершения текущего запроса.
let favoritesToggling = false;

const loadFavorites = async () => {
  const { data } = await getExperiencesFavoritesList();
  favorites.value = data ?? [];
};

const { viewHistory, loadViewHistory, sortByViewedAt } = useViewHistory();

const shouldShowRecentlyViewed = computed(() => Boolean(viewHistory.value.length) && isViewed.value);

const cardExperiences = computed(() =>
  rawExperiences.value.map((experience) => mapExperienceToExpCard(experience, movementTypeLabels.value)),
);

const isExperienceFavorite = (experienceId: number) => {
  return favorites.value.some((item) => item.item_id === experienceId);
};

// Отправляет намерение и добавляет недавно просмотренную экскурсию в избранное.
const handleSetFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'add',
      bootstrapData: getBootstrapData(),
      experience,
    });

    const favoriteItem = await addExperienceToFavorite({ path: { experienceId } });

    if (favoriteItem.data) {
      favorites.value = [...favorites.value, favoriteItem.data];
    }
  } finally {
    favoritesToggling = false;
  }
};

// Отправляет намерение и удаляет недавно просмотренную экскурсию из избранного.
const handleSetNotFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'remove',
      bootstrapData: getBootstrapData(),
      experience,
    });

    await removeExperienceFromFavorite({ path: { experienceId } });
    favorites.value = favorites.value.filter((item) => item.item_id !== experienceId);
  } finally {
    favoritesToggling = false;
  }
};

const handleToggleFavorite = (experienceId: number) => {
  const experience = rawExperiences.value.find((item) => item.id === experienceId);

  if (isExperienceFavorite(experienceId)) {
    void handleSetNotFavorite(experience);
    return;
  }

  void handleSetFavorite(experience);
};

const fetchData = async () => {
  loading.value = true;

  // Если нет истории просмотра, не загружаем данные
  if (!viewHistory.value.length) {
    loading.value = false;
    return;
  }

  let response;

  try {
    const idsString = viewHistory.value.map((el) => el.id).join(',');
    [response] = await Promise.all([
      getExperiencesByIds({
        query: {
          ids: idsString,
          // Показываем только доступные к бронированию экскурсии из истории просмотров.
          bookable: true,
          ...getDisplayCurrencyQuery(),
        },
      }),
      loadFavorites(),
    ]);
  } catch (e) {
    loading.value = false;
    isViewed.value = false;

    throw e;
  }

  const { data } = response;

  if (!data?.results.length) {
    isViewed.value = false;
  }

  rawExperiences.value = sortByViewedAt(data?.results ?? []);
  loading.value = false;
};

onMounted(() => {
  loadViewHistory();
  void fetchData();
});

const cardProps = useCarouselConfig<ExpCardExperience>({
  slides: cardExperiences,
  mobileMini: {
    cardWidth: 300,
    spaceBetween: 16,
    options: { initialSlide: 0, slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
  },
  tabletMobile: {
    slidesPerView: 2.9,
    spaceBetween: 16,
    options: {
      initialSlide: 0,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: { 767: { slidesPerGroup: 2 } },
    },
  },
  desktop: {
    slidesPerView: 4,
    spaceBetween: 16,
    options: { breakpoints: { 1023: { slidesPerGroup: 4 } } },
  },
});
</script>

<style lang="scss" scoped>
.title {
  color: var(--alias-color-text-default);
  text-decoration: none;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.mirror {
  transform: scaleX(-1);
}

.center {
  display: flex;
  justify-content: center;
}

.slide {
  flex-direction: column;
}

:deep(.btn-prev) {
  top: 25%;
  transform: translate(-50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.btn-next) {
  top: 25%;
  transform: translate(50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

.swiper-main-container:hover {
  :deep(.btn-prev) {
    opacity: 1;
  }

  :deep(.btn-next) {
    opacity: 1;
  }
}
</style>
